import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import * as Sentry from '@sentry/react';

const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const OpsComplianceStats = ({ period }) => {
    const [sipaData, setSipaData] = useState([]);
    const [prevSipaData, setPrevSipaData] = useState([]);
    const [certificateData, setCertificateData] = useState([]);
    const { apiFetch } = useAuth();

    // Parse period
    const periodStr = String(period ?? '');
    const year = periodStr.substring(0, 4);
    const month = periodStr.substring(4, 6);
    const monthIndex = month ? parseInt(month, 10) - 1 : 0;
    const currentHeader = month && year ? `${monthNames[monthIndex]} ${year}` : '';
    const prevHeader = month && year ? `${monthNames[monthIndex]} ${parseInt(year, 10) - 1}` : '';
    const prevPeriod = period - 1000;

    /**
     * Fetches SIPA scores data from the API
     */
    const fetchSipaData = useCallback(async () => {
        try {
            const [currentSipaRes, prevSipaRes] = await Promise.all([
                apiFetch(`/api/Ops/GetSipaReportScores?period=${period}`),
                apiFetch(`/api/Ops/GetSipaReportScores?period=${prevPeriod}`)
            ]);

            const currentSipaResult = await currentSipaRes.json();
            const prevSipaResult = await prevSipaRes.json();

            if (currentSipaResult.success && currentSipaResult.data) setSipaData(currentSipaResult.data);
            if (prevSipaResult.success && prevSipaResult.data) setPrevSipaData(prevSipaResult.data);

        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    }, [apiFetch, period, prevPeriod]);

    /**
     * Fetches outstanding certificate data from the API
     */
    const fetchCertificateData = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Ops/GetOutstandingCertificates');
            const result = await response.json();
            if (result.success && result.data) setCertificateData(result.data);
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    }, [apiFetch]);

    useEffect(() => {
        if (!period) return;
        fetchSipaData();
        fetchCertificateData();
    }, [period, fetchSipaData, fetchCertificateData]);

    // #region SIPA

    // Per property: avg of whichever scores are non-null. Portfolio: avg across all properties.
    const computeAvgSipa = (data) => {
        if (!data || data.length === 0) return 0;
        const scores = data
            .map(item => {
                const ga = item.scoreGeneralAppearance ?? null;
                const oc = item.scoreOperationsCompliance ?? null;
                if (ga !== null && oc !== null) return (ga + oc) / 2;
                return ga ?? oc ?? null;
            })
            .filter(s => s !== null);
        if (scores.length === 0) return 0;
        return Math.round((scores.reduce((sum, s) => sum + s, 0) / scores.length) * 100) / 100;
    };

    const avgSipaCurrent = computeAvgSipa(sipaData);
    const avgSipaPrev = computeAvgSipa(prevSipaData);

    const renderSipaTooltipTable = (data) => {
        if (!data || data.length === 0) return '<div>No data</div>';
        const details = data
            .map(item => {
                const ga = item.scoreGeneralAppearance ?? null;
                const oc = item.scoreOperationsCompliance ?? null;
                let score = null;
                if (ga !== null && oc !== null) score = Math.round(((ga + oc) / 2) * 100) / 100;
                else if (ga !== null) score = Math.round(ga * 100) / 100;
                else if (oc !== null) score = Math.round(oc * 100) / 100;
                return { propertyName: item.propertyName, score };
            })
            .filter(d => d.score !== null);

        if (!details.length) return '<div>No data</div>';

        return `
        <table style="border-collapse:collapse; table-layout:fixed; width:260px;">
            <thead>
                <tr>
                    <th style="padding:2px 8px; width:190px; text-align:left;">Property</th>
                    <th style="padding:2px 8px; width:70px; text-align:right;">SIPA Score</th>
                </tr>
            </thead>
            <tbody>
                ${details.map(d => `
                    <tr>
                        <td style="padding:2px 8px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${d.propertyName}">${d.propertyName}</td>
                        <td style="padding:2px 8px; text-align:right;">${d.score}%</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
    };

    // #endregion

    // #region Certificates

    // % of properties with no outstanding certs for the given certKey
    const computeCertCompliance = (data, certKey) => {
        if (!data || data.length === 0) return null;
        const compliantCount = data.filter(x => (x[certKey] ?? 0) === 0).length;
        return Math.round((compliantCount / data.length) * 10000) / 100;
    };

    const fireCompliance = computeCertCompliance(certificateData, 'outstandingFireClearanceCertificateCount');
    const occupancyCompliance = computeCertCompliance(certificateData, 'outstandingOccupancyCertificateCount');

    // Tooltip shows properties that have outstanding certs (count > 0); "All compliant" if none
    const renderCertTooltipTable = (data, certKey) => {
        if (!data || data.length === 0) return '<div>No data</div>';
        const outstanding = data.filter(x => (x[certKey] ?? 0) > 0);

        if (!outstanding.length) return '<div>All properties compliant</div>';

        return `
        <table style="border-collapse:collapse; table-layout:fixed; width:300px;">
            <thead>
                <tr>
                    <th style="padding:2px 8px; width:220px; text-align:left;">Property</th>
                    <th style="padding:2px 8px; width:80px; text-align:right;">Outstanding</th>
                </tr>
            </thead>
            <tbody>
                ${outstanding.map(x => `
                    <tr>
                        <td style="padding:2px 8px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${x.propertyName}">${x.propertyName}</td>
                        <td style="padding:2px 8px; text-align:right;">${x[certKey]}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
    };

    // #endregion

    return (
        <>
            <table>
                <thead>
                    <tr>
                        <th>Operations Compliance Statistics</th>
                        <th>{currentHeader}</th>
                        <th>{prevHeader}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>SIPA score</td>
                        <td
                            data-tooltip-id="ops-tt"
                            data-tooltip-content={renderSipaTooltipTable(sipaData)}
                        >
                            {avgSipaCurrent > 0 ? `${avgSipaCurrent}%` : '-'}
                        </td>
                        <td
                            data-tooltip-id="ops-tt"
                            data-tooltip-content={renderSipaTooltipTable(prevSipaData)}
                        >
                            {avgSipaPrev > 0 ? `${avgSipaPrev}%` : '0%'}
                        </td>
                    </tr>
                    <tr>
                        <td>Occupancy certificate compliance</td>
                        <td
                            data-tooltip-id="ops-tt"
                            data-tooltip-content={renderCertTooltipTable(certificateData, 'outstandingOccupancyCertificateCount')}
                        >
                            {occupancyCompliance !== null ? `${occupancyCompliance}%` : '-'}
                        </td>
                        <td>-</td>
                    </tr>
                    <tr>
                        <td>Fire clearance compliance</td>
                        <td
                            data-tooltip-id="ops-tt"
                            data-tooltip-content={renderCertTooltipTable(certificateData, 'outstandingFireClearanceCertificateCount')}
                        >
                            {fireCompliance !== null ? `${fireCompliance}%` : '-'}
                        </td>
                        <td>-</td>
                    </tr>
                </tbody>
            </table>
            <ReactTooltip
                id="ops-tt"
                place="bottom"
                variant="dark"
                delayShow={150}
                delayHide={0}
                openEvents={{ mouseenter: true }}
                closeEvents={{ mouseleave: true }}
                globalCloseEvents={{ escape: true, scroll: true, resize: true, clickOutsideAnchor: true }}
                style={{ maxWidth: '350px', padding: '6px' }}
                render={({ content }) =>
                    content ? <div dangerouslySetInnerHTML={{ __html: content }} /> : null
                }
            />
        </>
    );
};

export default OpsComplianceStats;